import { Component, OnInit, ChangeDetectorRef, ViewChild, ElementRef } from '@angular/core';
import { UntypedFormGroup, Validators } from '@angular/forms';
import { FormService } from "../../services/form.service";
import { GooglePlacesService } from "../../services/google-places.service";
import { debounceTime, distinctUntilChanged, switchMap, startWith, take } from "rxjs";
import { Observable } from 'rxjs';

@Component({
    selector: 'app-moving-to',
    templateUrl: './moving-to.component.html',
    styleUrls: ['./moving-to.component.scss']
})
export class MovingToComponent implements OnInit {

	movingTo !: UntypedFormGroup
	formGroupName : string = 'movingTo'
	postcodeEnabled = true;
	@ViewChild( 'addressInputEl' ) addressInputEl!: ElementRef;

	labelAddress: string = '';
	labelPostcode: string = '';
	fieldOrder: any = [];
	enableFields = false;
	stateFieldEnabled = false;
	
	// Autocomplete properties
	autocompleteSuggestions$: Observable<any[]> = new Observable();
	autocompleteEnabled = false;
	states = [
		{ code: 'AL', name: 'Alabama' },
		{ code: 'AK', name: 'Alaska' },
		{ code: 'AZ', name: 'Arizona' },
		{ code: 'AR', name: 'Arkansas' },
		{ code: 'CA', name: 'California' },
		{ code: 'CO', name: 'Colorado' },
		{ code: 'CT', name: 'Connecticut' },
		{ code: 'DE', name: 'Delaware' },
		{ code: 'FL', name: 'Florida' },
		{ code: 'GA', name: 'Georgia' },
		{ code: 'HI', name: 'Hawaii' },
		{ code: 'ID', name: 'Idaho' },
		{ code: 'IL', name: 'Illinois' },
		{ code: 'IN', name: 'Indiana' },
		{ code: 'IA', name: 'Iowa' },
		{ code: 'KS', name: 'Kansas' },
		{ code: 'KY', name: 'Kentucky' },
		{ code: 'LA', name: 'Louisiana' },
		{ code: 'ME', name: 'Maine' },
		{ code: 'MD', name: 'Maryland' },
		{ code: 'MA', name: 'Massachusetts' },
		{ code: 'MI', name: 'Michigan' },
		{ code: 'MN', name: 'Minnesota' },
		{ code: 'MS', name: 'Mississippi' },
		{ code: 'MO', name: 'Missouri' },
		{ code: 'MT', name: 'Montana' },
		{ code: 'NE', name: 'Nebraska' },
		{ code: 'NV', name: 'Nevada' },
		{ code: 'NH', name: 'New Hampshire' },
		{ code: 'NJ', name: 'New Jersey' },
		{ code: 'NM', name: 'New Mexico' },
		{ code: 'NY', name: 'New York' },
		{ code: 'NC', name: 'North Carolina' },
		{ code: 'ND', name: 'North Dakota' },
		{ code: 'OH', name: 'Ohio' },
		{ code: 'OK', name: 'Oklahoma' },
		{ code: 'OR', name: 'Oregon' },
		{ code: 'PA', name: 'Pennsylvania' },
		{ code: 'RI', name: 'Rhode Island' },
		{ code: 'SC', name: 'South Carolina' },
		{ code: 'SD', name: 'South Dakota' },
		{ code: 'TN', name: 'Tennessee' },
		{ code: 'TX', name: 'Texas' },
		{ code: 'UT', name: 'Utah' },
		{ code: 'VT', name: 'Vermont' },
		{ code: 'VA', name: 'Virginia' },
		{ code: 'WA', name: 'Washington' },
		{ code: 'WV', name: 'West Virginia' },
		{ code: 'WI', name: 'Wisconsin' },
		{ code: 'WY', name: 'Wyoming' }
	];

    constructor(
		public formService: FormService, 
		private cdr: ChangeDetectorRef,
		private googlePlacesService: GooglePlacesService
	) {}

    ngOnInit(): void {

        // Set moving to
        this.movingTo = this.formService.mainForm.get( this.formGroupName ) as UntypedFormGroup;

	    this.labelAddress = this.formService.getVariationTranslation( 'label-address');
	    this.labelPostcode = this.formService.getVariationTranslation( 'label-postcode');
		this.fieldOrder = this.formService.getFieldOrder( 'movingTo' );

		// Check if state field should be enabled
		this.stateFieldEnabled = this.formService.getSetting('stateFieldEnabled') === true;

		// Initial check
		this.onCountryChange( true );

		// Set initial state field validation
		this.updateStateFieldValidation();

	    this.formService.stepChange$.subscribe( currentFormGroupName => {

			// This enables the fields in the current step
			this.enableFields = currentFormGroupName === this.formGroupName;
	    } );
    }

    ngAfterViewInit() {

        // Initialize Google Places Autocomplete if enabled
        this.initializeGooglePlacesAutocomplete();
    }

	/**
	 * Initialize Google Places Autocomplete for this component
	 */
	private initializeGooglePlacesAutocomplete(): void {

		// Only disable Google Places Autocomplete if explicitly set to false
		if( this.formService.domainData?.enableGooglePlacesAutocomplete === false ) {
			return;
		}

		// Get country code for restriction
		const countryCode = this.movingTo.get('country')?.value;

		if( !countryCode ) {
			return;
		}

		// Initialize autocomplete using the service
		this.autocompleteSuggestions$ = this.googlePlacesService.initializeAutocomplete(
			this.movingTo,
			'address',
			countryCode
		);
	}

	/**
	 * Display function for autocomplete - shows the user's typed input
	 */
	displayWithTyped = (): string => this.movingTo.get('address')?.value || '';

	/**
	 * Handle autocomplete suggestion selection
	 */
	onSelectSuggestion(suggestion: any): void {

		console.log('onSelectSuggestion called with:', suggestion);

		if (!suggestion || !suggestion.placePrediction) {
			console.warn('Invalid suggestion received:', suggestion);
			return;
		}

		// Fetch place details and populate other fields
		this.googlePlacesService.handleSuggestionSelected( suggestion, this.movingTo );

		// Blur input after selection (desktop and mobile)
		setTimeout( () => {
			( document.activeElement as HTMLElement )?.blur();
			this.addressInputEl?.nativeElement.blur();
		}, 0 );
	}

    onSubmit() {

        this.formService.submitFormGroup( (<UntypedFormGroup>this.movingTo) );
    }

	onCountryChange( isInitialCall = false ) {

		var selectedCountry = this.movingTo.controls['country'].value;

		// Use verifyCountryCode here
		selectedCountry = this.formService.verifyCountryCode( selectedCountry );

		// Clean up old autocomplete instance
		this.googlePlacesService.cleanup();

		// Re-initialize Google Places Autocomplete with new country code
		this.initializeGooglePlacesAutocomplete();

		// Ireland, Qatar, United-arab-emirates, Hong kong, Curacao
		// Aruba, Cameroon, Ghana, Suriname, Sint maarten
		var excludedCountries = ['IE', 'QA', 'AE', 'HK', 'CW', 'AW', 'CM', 'GH', 'SR', 'SX'];
		var postcodeControl = (<UntypedFormGroup>this.movingTo).controls['postcode'];

		// If the country is one of the excluded countries
		if( excludedCountries.includes( selectedCountry ) ) {

			// Postcode is never required on MovingTo

			// Reset value
			postcodeControl.reset();

			// Change the field order
			this.fieldOrder = ['address','city','postcode'];

			// Hide the postcode field
			this.postcodeEnabled = false;
		}
		else {

			// Reset field order
			this.fieldOrder = this.formService.getFieldOrder( 'movingTo' );

			// Show the field
			this.postcodeEnabled = true;
		}

		console.log( 'attempt to get order from order by country to');

		this.fieldOrder = this.formService.getFieldOrderByCountry( selectedCountry, 'movingTo' );

		// Check if we can enable movingToExtended
		this.formService.checkToEnableMovingToExtended( isInitialCall );
		
		// Handle state field validation based on country selection
		this.updateStateFieldValidation();
	}

	/**
	 * Check if the state field should be enabled based on domain setting and country selection
	 */
	public isStateFieldEnabled(): boolean {
		return this.stateFieldEnabled && this.formService.verifyCountryCode(this.movingTo.controls['country'].value) === 'US';
	}

	/**
	 * Update state field validation based on current conditions
	 */
	private updateStateFieldValidation(): void {
		const stateControl = this.movingTo.controls['state'];
		
		// Safety check: ensure the state control exists
		if (!stateControl) {
			return;
		}
		
		if (this.isStateFieldEnabled()) {
			// Add required validator when state field should be shown
			stateControl.addValidators(Validators.required);
		} else {
			// Remove required validator when state field should be hidden
			stateControl.removeValidators(Validators.required);
			// Reset the state value when hiding the field
			stateControl.reset();
		}
		
		// Update validation
		stateControl.updateValueAndValidity();
	}
}
